import {
  Box,
  Button,
  Card,
  Container,
  Typography,
  styled,
} from "@mui/material";
import { Link } from "gatsby";
import React from "react";
import { richTextToPlainTextString } from "../../utils/richTextToPlainTextString";

import ArrowSvg from "../../images/case-study/arrowBlue.svg";
import DSvg from "../../images/case-study/d-icon.svg";

function cutLongText(text) {
  return typeof text === "string" && text.length > 80
    ? `${text.substring(0, 77)}...`
    : text;
}

const CaseStudyCard = ({ slug, isFull, cardBackground, ...props }) => {
  const case_study_path = `/success-stories/${slug}`;

  return isFull ? (
    <CaseStudyCardFull
      {...props}
      path={case_study_path}
      background={cardBackground}
    />
  ) : (
    <CaseStudyCardHalf
      {...props}
      path={case_study_path}
      background={cardBackground}
    />
  );
};

const CaseStudyCardFull = ({
  logoUrl,
  result,
  tags,
  challenge,
  path,
  heroTitle,
  background,
}) => {
  return (
    <CardContainer>
      <Box
        component="img"
        src={DSvg}
        style={{
          position: "absolute",
          left: "25px",
          top: "25px",
        }}
        loading="lazy"
      />
      <UpperContainer background={background}>
        <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
          <Box
            component="img"
            loading="lazy"
            src={`https:${logoUrl}`}
            sx={{
              width: "90%",
              objectFit: "contain",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              maxWidth: "200px",
              height: { xs: "40px", sm: "60px" },
              maxHeight: "100%",
            }}
          />
        </Box>
        <Box
          sx={{
            width: { xs: "2px", sm: "3px" },
            height: { xs: "40px", sm: "60px" },
            marginLeft: { xs: "15px", sm: "25px" },
            marginRight: { xs: "15px", sm: "25px" },
            backgroundColor: "#457584",
          }}
        />
        <Box style={{ width: "100%" }}>
          <ResultsTypography>{heroTitle}</ResultsTypography>
        </Box>
      </UpperContainer>
      <LowerContainer>
        <Box
          style={{
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          {tags.map((tag, index) => (
            <>
              <TagsText>{tag}</TagsText>
              {index < tags.length - 1 && <TagsText>|</TagsText>}
            </>
          ))}
        </Box>
        <ResultsText>{`${result?.value} ${result?.improved}`}</ResultsText>
        <ChallengeText>
          {cutLongText(richTextToPlainTextString(challenge))}
        </ChallengeText>

        <StyledButton component={Link} to={path}>
          <Typography
            style={{
              fontWeight: 600,
            }}
          >
            View Case
          </Typography>
          <Box
            component="img"
            loading="lazy"
            src={ArrowSvg}
            sx={{ height: "12px", marginLeft: "12px" }}
          />
        </StyledButton>
      </LowerContainer>
    </CardContainer>
  );
};

const CaseStudyCardHalf = ({
  logoUrl,
  tags,
  challenge,
  path,
  background,
  heroTitle,
}) => {
  return (
    <CardContainerMini>
      <LeftContainer background={background}>
        <Box
          component="img"
          loading="lazy"
          src={DSvg}
          style={{
            position: "absolute",
            left: "25px",
            top: "25px",
          }}
        />
        <Box
          component="img"
          src={`https:${logoUrl}`}
          loading="lazy"
          sx={{
            width: "90%",
            objectFit: "contain",
            position: "absolute",
            bottom: "0",
            left: "50%",
            transform: "translate(-50%,-50%)",
            maxWidth: "100px",
            height: { xs: "40px", sm: "60px" },
            maxHeight: "100%",
          }}
        />
      </LeftContainer>
      <RightContainer>
        <Box
          style={{
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          {tags.map((tag, index) => (
            <>
              <TagsText>{tag}</TagsText>
              {index < tags.length - 1 && <TagsText>|</TagsText>}
            </>
          ))}
        </Box>
        <ResultsText>{heroTitle}</ResultsText>
        <ChallengeTextMini>
          {cutLongText(richTextToPlainTextString(challenge))}
        </ChallengeTextMini>
        <StyledButton component={Link} to={path}>
          <Typography
            style={{
              fontWeight: 600,
            }}
          >
            View Case
          </Typography>
          <Box
            component="img"
            loading="lazy"
            src={ArrowSvg}
            sx={{ height: "12px", marginLeft: "12px" }}
          />
        </StyledButton>
      </RightContainer>
    </CardContainerMini>
  );
};

export default CaseStudyCard;

// styles
const CardContainerMini = styled(Card)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  minWidth: "580px",
  maxWidth: "580px !important",
  margin: "0 auto",
  padding: "0 !important",
  boxShadow: "rgba(0, 0, 0, 0.2) 0px 2px 4px 0px",
  borderRadius: "16px",
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    backgroundColor: "#fff",
    backgroundImage: "none",
    width: "auto",
    maxWidth: "537px",
    borderBottom: "5px solid #457584",
    padding: "10px",
  },
}));

const CardContainer = styled(Card)(({ theme }) => ({
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  minWidth: "580px",
  maxWidth: "580px !important",
  margin: "0auto",
  padding: 0,
  borderRadius: "16px",
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
  },
}));

const UpperContainer = styled(Container)(({ theme, background }) => ({
  backgroundImage: `url(${background})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "#457584",
  height: "193px",
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  padding: "50px !important",
  alignItems: "center",
  justifyContent: "center",
  justifyItems: "center",
  borderRadius: theme.spacing(2),
  [theme.breakpoints.down("lg")]: {
    padding: "20px !important",
  },
}));

const ResultsTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "24px",
  lineHeight: "30px",
  color: "#457584",
  [theme.breakpoints.down("lg")]: {
    fontSize: "16px !important",
  },
}));

const TagsText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  textAlign: "left",
  color: "#457584",
  marginRight: "5px",
  marginBottom: "11px",
  [theme.breakpoints.down("lg")]: {
    marginBottom: 0,
  },
}));

const ResultsText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  textAlign: "left",
  fontSize: "24px",
  lineHeight: "36px",
  color: "#191616",
  marginBottom: "6px",
  [theme.breakpoints.down("lg")]: {
    marginBottom: 0,
  },
}));

const ChallengeText = styled(Typography)(({ theme }) => ({
  textAlign: "left",
  color: "#B4B4B4",
  marginBottom: "20px",
  [theme.breakpoints.down("lg")]: {
    marginBottom: 0,
  },
}));

const LowerContainer = styled(Container)(({ theme }) => ({
  height: "235px",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "50px !important",
  [theme.breakpoints.down("lg")]: {
    padding: "20px !important",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 0,
  backgroundColor: "#F5FAFC",
  color: "#509EB9",
  padding: 0,
  width: "153px",
  height: "52px",
}));

const LeftContainer = styled(Container)(({ theme, background }) => ({
  backgroundImage: `url(${background})`,
  backgroundSize: "cover",
  backgroundPosition: "center center",
  color: "#457584",
  height: "100%",
  width: "120px",
  display: "flex",
  position: "relative",
  padding: 0,
  paddingBottom: theme.spacing(4),
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-end",
  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

const RightContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "24px !important",
  [theme.breakpoints.down("lg")]: {},
}));

const ChallengeTextMini = styled(Typography)(({ theme }) => ({
  textAlign: "left",
  color: "#B4B4B4",
  marginBottom: "5px",
  [theme.breakpoints.down("lg")]: {
    marginBottom: 0,
  },
}));
